import React from 'react'
import { connect } from 'react-redux'
import styled, { css } from 'react-emotion'
import { color } from 'stylist'
import { Editor, getEventTransfer } from 'slate-react'
import { Value } from 'slate'
import Plugins from './plugins'
import { sanitizeText } from 'utils/helpers'
import { getDocument, updateDocument } from 'utils/api'
import LoadError from './Alerts/LoadError'
import URLSearchParams from 'url-search-params'
import debounce from 'lodash/debounce'
import Header from './Header'
import { updateSurveyMeta } from 'actions'

const params = new URLSearchParams(window.location.search)
const offlineMode = params.get('offline') && params.get('offline') !== '0'
const AUTOSAVE_DEBOUNCE_DELAY = 1000

const schema = {
  document: {
    nodes: [
      {
        match: [
          { type: 'lineGroup' },
          { type: 'singleSelect' },
          { type: 'multiSelect' },
          { type: 'open' },
          { type: 'number' },
          { type: 'comment' },
          { type: 'pageBreak' }
        ]
      }
    ],
    last: {
      text: /^$/
    },
    normalize: (editor, { code, node, child, index }) => {
      switch (code) {
        case 'child_type_invalid':
          editor.setNodeByKey(child.key, 'lineGroup')
          break
        case 'last_child_text_invalid':
          editor.insertLineGroupAfter(child)
          break
        default:
          break
      }
    }
  }
}

const initialValue = Value.fromJSON({
  document: {
    nodes: [
      {
        object: 'block',
        type: 'paragraph',
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                text: ''
              }
            ]
          }
        ]
      }
    ]
  }
})

const editorStyle = {
  position: 'absolute',
  left: '17px',
  right: '0',
  minHeight: '100%',
  width: '920px',
  margin: '0 auto',
  paddingTop: '70px',
  paddingBottom: '70px',
  paddingLeft: '20px',
  paddingRight: '20px',
  backgroundColor: 'white',
  fontSize: '16px',
  lineHeight: '1.43'
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: ${({ innerRef }) => {
    const { current: el } = innerRef
    return `calc(100vh - ${el ? el.offsetTop : 0}px)`
  }};
  overflow-y: hidden;
  margin: 0;
`

const EditorWrapper = styled.div`
  position: relative;
  flex: auto;
  height: 100%;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    -webkit-appearance: none;
  }
`

const _QuestionCounts = ({ identified, unidentified, className }) => (
  <p className={className}>
    <span>{identified} Identified</span> <span>|</span>{' '}
    <span>{unidentified} Unidentified</span>
  </p>
)

const QuestionCounts = styled(_QuestionCounts)`
  width: 920px;
  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;

  color: ${color('slate')};
  font-size: 16px;

  span {
    display: inline-block;

    &:nth-child(2) {
      margin-left: 10px;
      margin-right: 10px;
    }

    &:last-child {
      color: #91465f;
    }
  }
`

const plugins = Plugins.map(p => p())

class SlateEditor extends React.Component {
  state = {
    value: initialValue,
    predictive: true,
    canAutosave: false,
    loadError: false
  }

  constructor(props) {
    super(props)

    this.wrapper = React.createRef()
  }

  componentDidMount = () => {
    this.loadContent()
  }

  componentDidUpdate = prevProps => {
    if (prevProps.title !== this.props.title) {
      this.saveJson(
        this.editor.serialize(this.editor.value, { title: this.props.title })
      )
    }
  }

  onKeyDown = (ev, editor, next) => {
    // Shift-Escape toggles predictive mode
    if (
      ['development', 'staging'].includes(process.env.REACT_APP_ENV) &&
      ev.shiftKey &&
      ev.key === 'Escape'
    ) {
      this.setState({ predictive: !this.state.predictive }, () => {
        editor.predictUnstructured()
      })
      return
    }
    next()
  }

  onChange = ({ value }) => {
    let changed = value.document !== this.state.value.document

    this.setState({ value }, () => {
      if (changed) {
        const completionStatus = this.editor.getCompletionStatus()

        this.props.updateSurveyMeta({
          counts: {
            identified: completionStatus.structured,
            unidentified: completionStatus.total - completionStatus.structured
          }
        })
        this.autosave()
      }
    })
  }

  async loadContent() {
    if (offlineMode) {
      this.setState(
        {
          value: initialValue
        },
        this.editor.focus
      )
      return
    }

    try {
      const response = await getDocument()
      const data = await response.data

      const surveyJson = data.surveyJson
        ? JSON.parse(data.surveyJson)
        : JSON.parse(data.content)

      const value = this.editor.deserialize(surveyJson)

      this.updateMeta({
        title: surveyJson.title,
        callbackUrl: data.callback_url,
        backConfig: {
          text: data.back_text,
          url: data.back_url
        }
      })

      this.setState(
        {
          value: value,
          canAutosave: true
        },
        () => {
          this.editor.predictUnstructured()
        }
      )
    } catch (error) {
      this.setState(
        {
          value: initialValue,
          loadError: true
        },
        this.editor.focus
      )
    }
  }

  autosave = debounce(() => {
    if (!this.editor || offlineMode || !this.state.canAutosave) {
      return
    }

    this.saveJson(
      this.editor.serialize(this.editor.value, { title: this.props.title })
    )
  }, AUTOSAVE_DEBOUNCE_DELAY)

  saveSurvey = options => {
    const { force, excludeUnidentified = false } = options
    const lineGroupNode = this.editor.value.document.nodes.find(
      node => node.text.trim() && node.type === 'lineGroup'
    )

    if (lineGroupNode && !force) {
      this.editor.moveToStartOfNode(lineGroupNode).focus()
      this.editor.value.document.nodes.forEach(node => {
        if (node.text.trim()) {
          this.editor.setNodeByKey(node.key, {
            data: { highlight: true }
          })
        }
      })
    } else {
      // Remove all unstructured content
      if (excludeUnidentified) {
        this.editor.pruneUnstructured()
      }

      // Save serialized content back to the database:
      this.saveJson(
        this.editor.serialize(this.editor.value, { title: this.props.title })
      )

      // Redirect to final editor.
      window.location.replace(this.props.callbackUrl)
    }
  }

  saveJson = async content => {
    try {
      return await updateDocument(content)
    } catch (error) {
      this.setState({
        loadError: true
      })
    }
  }

  updateMeta = payload => {
    this.props.updateSurveyMeta(payload)
    this.saveJson(
      this.editor.serialize(this.editor.value, { title: payload.title })
    )
  }

  onPaste = (ev, editor, next) => {
    const transfer = getEventTransfer(ev)
    const text = sanitizeText(transfer.text)

    !text ||
      text
        .split('\n\n')
        .filter(textGroup => textGroup.trim())
        .forEach(textGroup => {
          editor.insertLineGroup(textGroup)
          editor.insertLineGroup('')
        })

    editor.predictUnstructured()
  }

  render() {
    return (
      <Container innerRef={this.wrapper}>
        <Header saveSurvey={this.saveSurvey} />
        <QuestionCounts
          identified={this.props.counts.identified}
          unidentified={this.props.counts.unidentified}
        />
        <EditorWrapper
          id="editor-wrapper"
          innerRef={el => (this.el = el)}
          onMouseOver={this.onMouseOver}
        >
          <LoadError loadError={this.state.loadError} />
          <Editor
            autoFocus={true}
            schema={schema}
            plugins={plugins}
            value={this.state.value}
            onFocus={ev => {
              // HACK: This is workaround for a Slate Firefox bug: https://github.com/ianstormtaylor/slate/pull/2236
              // Once we've upgraded to a version that incorporates this fix please remove.
            }}
            onKeyDown={this.onKeyDown}
            onChange={this.onChange}
            onPaste={this.onPaste}
            ref={editor => (this.editor = editor)}
            className={css(editorStyle)}
            placeholder="Your survey goes here..."
            isPredictive={this.state.predictive}
          />
        </EditorWrapper>
      </Container>
    )
  }
}

const mapStateToProps = state => {
  return state.surveyMeta
}

const mapDispatchToProps = dispatch => {
  return {
    updateSurveyMeta: payload => {
      dispatch(updateSurveyMeta(payload))
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SlateEditor)



// WEBPACK FOOTER //
// ./src/components/Editor/Editor.js